/**
 * This is a forked version of the NDS AutocompleteModal component,
 * which has been modified to support non-selectable "headings" in its
 * composition, as well as to support searching by a "searchValue" prop on the
 * select child Items.
 *
 * This particular file has no changes from the original
 * besides differences in import paths or linting.
 */

import React, { useState, useMemo } from "react";
import { Popover } from "@narmi/design_system";
import Item from "./Item";
import AutoComplete, { AutoCompleteItem } from "./AutoComplete";

interface AutocompleteModalProps {
  /** Input label */
  inputLabel: string;
  /** Trigger element */
  trigger: React.ReactNode | ((isActive: boolean) => React.ReactNode);
  /** AutocompleteModal.Item children */
  children: React.ReactNode[];
  /** Selection change event callback */
  onChange?: (value: string) => void;
  /** Input change event callback - called with value of selected item */
  onInputChange?: (value: string) => void;
  /** Optional pinned footer content; use for action buttons */
  footerContent?: React.ReactNode;
}

/**
 * A simplified Combobox component that renders in a Popup.
 * Useful for inline editing of things like table items where we want to
 * provide an autocomplete of possible values.
 *
 * Allows single selection only. The selected value will be rendered in the
 * trigger area.
 */
const AutocompleteModal = ({
  inputLabel,
  children,
  trigger,
  onChange = () => {},
  onInputChange = () => {},
  footerContent,
}: AutocompleteModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<React.ReactElement<any> | null>(null);
  const items = useMemo(() => React.Children.toArray(children), [children]);

  const handleSelection = (item: React.ReactElement<any>) => {
    setIsOpen(false);
    setSelectedItem(item);
    onChange(item.props.value);
  };

  const togglePopover = () => {
    setIsOpen((o) => !o);
  };

  const closePopover = () => {
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onUserDismiss={closePopover}
      onUserEnable={togglePopover}
      side="top"
      autoFocus={true}
      hasShadow={false}
      content={
        <div className="nds-autocompleteModal-positionedEl">
          <AutoComplete
            items={items as AutoCompleteItem[]}
            label={inputLabel}
            onChange={handleSelection}
            onInputChange={onInputChange}
            footerContent={footerContent}
            selectedItem={selectedItem as AutoCompleteItem}
          />
        </div>
      }
    >
      <span style={{ cursor: "pointer" }}>
        {typeof trigger === "function" ? trigger(isOpen) : trigger}
      </span>
    </Popover>
  );
};

AutocompleteModal.Item = Item;
export default AutocompleteModal;
